import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks';
import {
  SUPPORTED_LOCALES,
  applyLocale,
  getStoredLocale,
  normalizeLocale,
} from '../i18n';
import { cn } from '../utils/cn';

const LanguageSwitcher = ({ className }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { updateProfile } = useProfile();
  const [saving, setSaving] = useState(false);
  const currentLocale = normalizeLocale(i18n.language || getStoredLocale());

  const handleChange = async (locale) => {
    const next = normalizeLocale(locale);
    if (next === currentLocale || saving) return;
    setSaving(true);
    try {
      await applyLocale(next);
      if (user?.id) {
        const { error } = await updateProfile({ preferred_locale: next });
        if (error) console.error('Failed to save language preference:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn('inline-flex items-center rounded-lg border border-border bg-card p-0.5', className)}
      role="group"
      aria-label={t('languages.label')}
    >
      {SUPPORTED_LOCALES.map((code) => {
        const active = code === currentLocale;
        return (
          <button
            key={code}
            type="button"
            disabled={saving}
            onClick={() => handleChange(code)}
            className={cn(
              'min-w-[2.5rem] rounded-md px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={active}
          >
            {code}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
